import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Scale } from "lucide-react";
import { toast } from "sonner";

export default function PostToBinDialog({ load, stockpiles, onClose, onSaved }) {
  const [stockpileId, setStockpileId] = useState(load.stockpile_id || "");
  const [postedTons, setPostedTons] = useState(load.posted_tons ?? load.weight_tons ?? 0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isRepost = !!load.stockpile_id;

  const selected = stockpiles.find((sp) => sp.id === stockpileId);
  const tonsNum = parseFloat(postedTons) || 0;
  // Effective delta: repositioning within the same bin only counts the delta.
  const effective = selected && selected.id === load.stockpile_id
    ? tonsNum - (parseFloat(load.posted_tons) || 0)
    : tonsNum;
  const cap = selected ? parseFloat(selected.capacity_tons) || 0 : 0;
  const cur = selected ? parseFloat(selected.current_tons) || 0 : 0;
  const wouldOverflow = selected && cap > 0 && (cur + effective) > cap;
  const overBy = wouldOverflow ? (cur + effective - cap).toFixed(1) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!stockpileId) { toast.error("Choose a destination bin"); return; }
    if (wouldOverflow) {
      toast.error(`Bin ${selected.bin_number} would overflow by ${overBy}t. Choose a different bin or split the load.`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/loads/${load.id}/post-to-bin`, {
        stockpile_id: stockpileId,
        posted_tons: tonsNum,
        notes,
      });
      toast.success(isRepost ? "Posted tons updated" : "Load posted to bin");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="label-caps flex items-center gap-1.5"><Scale className="w-3 h-3" /> Scale-house</div>
            <div className="font-display font-bold text-xl">{isRepost ? "Adjust Bin / Tons" : "Post to Bin"}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-mono">{load.bol_number} · {load.weight_tons}t manifested</div>
          </div>
          <button onClick={onClose} data-testid="close-post-bin"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Destination Bin">
            <select
              required
              value={stockpileId}
              onChange={(e) => setStockpileId(e.target.value)}
              className="input-sharp"
              data-testid="post-bin-select"
            >
              <option value="">Select…</option>
              {stockpiles.map((sp) => {
                const spCap = parseFloat(sp.capacity_tons) || 0;
                const spCur = parseFloat(sp.current_tons) || 0;
                const remaining = Math.max(0, spCap - spCur);
                const isFull = spCap > 0 && remaining < tonsNum && sp.id !== load.stockpile_id;
                return (
                  <option key={sp.id} value={sp.id}>
                    {sp.bin_number} · {sp.name} · {sp.classification} · {spCur.toFixed(1)}/{spCap}t{isFull ? "  ⚠ FULL" : ""}
                  </option>
                );
              })}
            </select>
            {stockpiles.length === 0 && (
              <div className="text-[11px] text-red-600 mt-1">No bins exist at this drop-off site yet. Create one under Stockpiles first.</div>
            )}
            {selected && cap > 0 && (
              <div className="text-[11px] text-gray-500 mt-1 font-mono">
                Bin capacity: {cur.toFixed(1)} / {cap}t{" · "}
                <span className={wouldOverflow ? "text-red-600 font-bold" : "text-emerald-700"}>
                  {wouldOverflow ? `over by ${overBy}t` : `room for ${(cap - cur).toFixed(1)}t`}
                </span>
              </div>
            )}
          </F>
          <F label="Posted Tons (actual scale weight)">
            <input
              type="number"
              step="0.1"
              required
              value={postedTons}
              onChange={(e) => setPostedTons(e.target.value)}
              className="input-sharp font-mono"
              data-testid="posted-tons-input"
            />
            <div className="text-[11px] text-gray-500 mt-1">Defaults to the manifest weight. Adjust if scale reading differs.</div>
          </F>
          <F label="Notes (optional)">
            <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="input-sharp" data-testid="post-notes-input" />
          </F>
          {isRepost && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2">
              This load is already posted to bin <span className="font-mono font-bold">{load.bin_number}</span> ({(load.posted_tons ?? 0)}t). Submitting will reverse that entry and post fresh.
            </div>
          )}
          {wouldOverflow && (
            <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 p-2" data-testid="capacity-warning">
              Bin <span className="font-mono font-bold">{selected.bin_number}</span> cannot hold this load — over by <span className="font-mono font-bold">{overBy}t</span>. Choose a different bin or split the load.
            </div>
          )}
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy || stockpiles.length === 0 || wouldOverflow} data-testid="submit-post-bin" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "Posting…" : (isRepost ? "Update Posting" : "Post to Bin")}
            </button>
          </div>
        </form>
        <style>{`.input-sharp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; background: white; } .input-sharp:focus { outline: none; border-color: #111827; box-shadow: 0 0 0 1px #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
